import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { rest } from '@/lib/hono-client';

export type AuthUser = {
  id: string;
  email: string;
  username: string;
  role?: string;
};

const keys = {
  all: ['auth'] as const,
  whoami: () => [...keys.all, 'whoami'] as const,
  session: () => [...keys.all, 'session'] as const,
};

export const authQueryOptions = {
  queryKey: keys.whoami(),
  queryFn: async (): Promise<AuthUser | null> => {
    const res = await rest.api.auth.whoami.$get();
    if (!res.ok) return null;
    return res.json().then((data) => data.user);
  },
  // refetchOnWindowFocus: false,
  // retry: (failureCount: number, error: Error) => {
  //   return error.message !== 'Not authenticated' && failureCount < 2;
  // },
};

export function useAuthWhoamiQuery() {
  return useQuery(authQueryOptions);
}

export function useAuthWhoami() {
  const query = useAuthWhoamiQuery();

  return {
    user: query.data,
    isAuthenticated: !!query.data,
    isAuthLoading: query.isLoading,
  };
}

type SignupPayload = {
  email: string;
  username: string;
  password: string;
};

export const useLoginMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (input: SignupPayload) => {
      const res = await rest.api.auth.login.$post({ json: input });
      return res.json();
    },
    onSuccess: async (data) => {
      queryClient.clear();
      queryClient.setQueryData(keys.session(), data);
      await navigate({ to: '/' });
    },
  });
};

export function useSignupMutation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (input: SignupPayload) => {
      const res = await rest.api.auth.signup.$post({ json: input });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error.msg || 'Signup failed');
      }
      return res.json();
    },
    onSuccess: async (data) => {
      queryClient.clear();
      queryClient.setQueryData(keys.session(), data);
      await navigate({ to: '/login' });
    },
  });
}

type SignupFormValues = {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
};

export function useSignup() {
  const signup = useSignupMutation();

  const signupAsync = async (values: SignupFormValues) => {
    if (values.password !== values.confirmPassword) {
      throw new Error('Las contraseñas no coinciden');
    }

    return signup.mutateAsync({
      username: values.username,
      email: values.email,
      password: values.password,
    });
  };

  return {
    signupAsync,
    isSignupPending: signup.isPending,
    isSignupSuccess: signup.isSuccess,
    signupError: signup.error,
  };
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      await rest.api.auth.logout.$post();
    },
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: '/login' });
    },
  });
}
